import { globals } from 'config/globals';
import { device } from 'utils/device';
import { ImagePixelDataGrid } from 'utils/ImagePixelDataGrid';
import HomeFieldConfig from 'app/views/home/webgl/particles/config/HomeFieldConfig';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import HalftonePersonPixelCache from 'app/data/HomePixelCache/HalftonePersonPixelCache';
import { FLAG_ACTIVE } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { ScreensRadialHalftone } from 'app/views/home/webgl/particlesOptimised/departments/screens/ScreensHalftone';

import type { ScreensContext } from 'app/views/home/webgl/particlesOptimised/departments/screens/ScreensContext';
import type { DepartmentPhase } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * RadialHalftoneImagePhase — the reference ScreensDepartment's
 * 'radialHalftoneImage' section (_doRadialHalftoneImage), phase-file'd in the
 * screens retrofit. The BODY is the verbatim stage-5 lane port that lived in
 * particlesOptimised ScreensDepartment._doRadialHalftoneImage — the retrofit
 * moved it, it did not change it.
 *
 * VISUAL: the halftone person — a radial CMY-ish dot grid sampled from the
 * cached portrait image builds up point by point inside the lit TVs, breathes,
 * then sweeps upward off-screen (the ComboCounter offY beat) while the TVs
 * shut off one by one.
 *
 * Ownership decision (see ScreensContext header): the ScreensRadialHalftone
 * instance is PHASE-OWNED — one fresh instance per pass exactly like the
 * reference (`new RadialHalftoneImage()` per _doRadialHalftoneImage call;
 * points/init config/scale lookups reset per pass). tick() forwards
 * deltaTime to it — the committed department did this from its own tick,
 * guarded on the instance; hide() nulls it (the reference nulls
 * _radialHalftoneImage in the _nextScreensSection preamble and on stop —
 * both land on hide() through the runner).
 *
 * NOTE start() is NOT a complete visual arm: _createPoint never sets the
 * scale/alpha EASE lanes — it rides on the creation-state defaults
 * (ParticleCore: x/y/scale 1, alpha 3) or whatever the predecessor phase
 * left (production: the swirl's easeScale 30 / easeAlpha 2). Known
 * hidden-coupling case for the workbench's isolation seeding.
 */
export class RadialHalftoneImagePhase implements DepartmentPhase {
	public readonly name = 'radialHalftoneImage';

	private _ctx: ScreensContext;
	private _radialHalftoneImage: ScreensRadialHalftone = null;

	constructor(ctx: ScreensContext) {
		this._ctx = ctx;
	}

	public init(): void {
		//No one-time setup — the halftone rebuilds per pass (reference).
	}

	/** _doRadialHalftoneImage, verbatim. */
	public start(): void {
		const ctx = this._ctx;
		const core = ctx.core;
		const range = ctx.range;
		const deps = ctx.deps;

		globals.colourPhase = globals.colourPhase === 'yellow' ? 'green' : 'yellow';
		globals.screensZoneScale = deps.getSizes().zones.screens.scale;

		this._radialHalftoneImage = new ScreensRadialHalftone(core, deps);

		let totalParticles = HomeFieldConfig.screensHalftoneParticles;
		if (device.innerWidth < 700) {
			totalParticles *= HomeFieldConfig.screensHalftoneParticlesMobileScale;
		}

		this._radialHalftoneImage.setPool(range.start, Math.min(totalParticles, range.count));

		//The slice(totalParticles) leftovers deactivate:
		for (let n = totalParticles; n < range.count; n++) {
			core.stateFlags[range.start + n] &= ~FLAG_ACTIVE;
		}

		const imageGrid = new ImagePixelDataGrid();
		imageGrid.setDataFromCache(HalftonePersonPixelCache.data);
		this._radialHalftoneImage.setImgData(imageGrid);
		this._radialHalftoneImage.offsetX = -0.045;
		this._radialHalftoneImage.offsetY = 0.3;

		HomeScreens.sceneProps = deps.sceneProps;
		const relativeSizes = HomeScreens.getTotalRelativeSizes();

		//Both create() calls kept — only the second's blue grid survives
		//(reference bug, see ScreensHalftone):
		this._radialHalftoneImage.create(0.055, 0.055, 0.0035);
		this._radialHalftoneImage.create(relativeSizes.w / 15, relativeSizes.w / 15, 0.0035);

		const reduce = ctx.reduceFirstPass ? 0.55 : 1;
		const halftone = this._radialHalftoneImage;

		ctx.counters.clear();
		ctx.counters.add(() => halftone.offY(), 6000 * reduce);
		ctx.counters.add(() => {
			HomeScreens.off('bottomRight');
			HomeScreens.off('bottomLeft');
		}, 7000 * reduce);
		ctx.counters.add(() => HomeScreens.off('topRight'), 8000 * reduce);
		ctx.counters.add(() => HomeScreens.off('topLeft'), 8200 * reduce);

		HomeScreens.allOn(100);
	}

	/** The committed department's guarded halftone tick (staggered point
	 *  creation off the per-entry counters). */
	public tick(): void {
		if (this._radialHalftoneImage) {
			this._radialHalftoneImage.tick(this._ctx.deps.context.deltaTime);
		}
	}

	/** Reference: _radialHalftoneImage = null in the _nextScreensSection
	 *  preamble AND on stop — both are hide() here. Pending ComboCounter
	 *  entries deliberately survive (shared counter — see ScreensContext);
	 *  safe on a never-started phase. */
	public hide(): void {
		this._radialHalftoneImage = null;
	}

	public reset(): void {
		//No persistent phase state — the instance dies in hide(); the module
		//globals it flips (colourPhase) persist ON PURPOSE (the reference's
		//yellow/green alternation carries across scene stops).
	}
}
